import type { WhatsappOriginalTemplate } from 'types/templates';
import type { TemplateVarsData, TemplateVarsDataItem } from 'types/templates/templates-menu';
import { varsRegular } from 'utils/regexes';

/**
 * כש־GetTemplateWithMediaDataFromObject לא מחזיר spec — בונים רשימת פרמטרים ממבנה התבנית המקורי (כמו תצוגה מקדימה בקטלוג).
 * משמש רק למילוי שדות בממשק.
 */
export function buildTemplateVarsDataFromOriginal(original: WhatsappOriginalTemplate): TemplateVarsData {
  const data: TemplateVarsData = [];
  const comps = original.components ?? [];

  const header = comps.find((c) => c.type === 'HEADER');
  if (header && 'format' in header && header.format && header.format !== 'TEXT') {
    const fmt = String(header.format).toLowerCase();
    const label = fmt === 'image' || fmt === 'video' || fmt === 'document' ? fmt : 'image';
    data.push({
      name: 'data',
      label: label as 'image' | 'video' | 'document',
      options: null,
      required: true,
      type: 'text',
      text: '',
    });
  }

  const pushUniqueVars = (text: string | null | undefined, role: 'header' | 'body' | 'footer') => {
    if (!text) return;
    const matches = text.match(varsRegular) || [];
    const seen = new Set<string>();
    matches.forEach((variable) => {
      const variableNumber = variable.match(/\d+/)?.[0];
      if (!variableNumber || seen.has(variableNumber)) return;
      seen.add(variableNumber);
      const n = parseInt(variableNumber, 10);
      if (role === 'body' && (n < 1 || n > 10)) return;
      if (role === 'header' && n !== 1) return;
      const prefix = role === 'header' ? 'header' : role === 'footer' ? 'footer' : 'body';
      const paramName = `${prefix}_${variableNumber}`;
      data.push({
        name: paramName,
        label: `{{${variableNumber}}}`,
        options: null,
        required: false,
        type: 'text',
        text: '',
      } as TemplateVarsDataItem);
    });
  };

  if (header && 'text' in header && header.format === 'TEXT') {
    pushUniqueVars(header.text, 'header');
  }

  const body = comps.find((c) => c.type === 'BODY');
  pushUniqueVars(body?.text ?? null, 'body');

  const footer = comps.find((c) => c.type === 'FOOTER');
  pushUniqueVars(footer?.text ?? null, 'footer');

  const buttons = comps.find((c) => c.type === 'BUTTONS');
  if (buttons && 'buttons' in buttons && Array.isArray(buttons.buttons)) {
    let btnIndex = 0;
    for (const btn of buttons.buttons) {
      const url = btn.url ?? '';
      if (!/\{\{\d+\}\}/.test(url)) continue;
      btnIndex += 1;
      if (btnIndex > 10) break;
      data.push({
        name: `buttons_${btnIndex}`,
        label: 'text',
        options: null,
        required: false,
        type: 'text',
        text: '',
      } as TemplateVarsDataItem);
    }
  }

  return data;
}
